import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pause, Play, Globe2 } from "lucide-react";
import { HomeMap } from "@/components/map/HomeMap";
import { zoomForRadius } from "@/components/map/geo";
import { useBases, useListings } from "@/hooks/useData";
import { BaseInfoCard } from "./BaseInfoCard";
import { BaseGlyph, baseCamera, homesNear, pairCamera, REGION_VIEWS } from "./baseUtils";

const REGIONS = Object.keys(REGION_VIEWS);
const isPhone = () => window.innerWidth < 640;
/** Keep the camera target in the part of the map not covered by the panels / bottom card. */
const viewPad = () => (isPhone() ? { top: 130, bottom: 400, left: 0, right: 0 } : { top: 0, bottom: 0, left: 0, right: 0 });
// fitBounds adds this on top of the map's current padding (viewPad, set by the base fly-in).
const pairPad = () => (isPhone() ? { top: 30, bottom: 30, left: 40, right: 40 } : { top: 140, bottom: 140, left: 380, right: 500 });

const BaseList = ({ bases, selected, onSelect }) => (
  <ul className="space-y-1" data-testid="base-list">
    {bases.map((b) => (
      <li key={b.id}>
        <button
          type="button"
          onClick={() => onSelect(b.id)}
          data-testid={`base-list-item-${b.id}`}
          className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors ${
            selected === b.id ? "bg-ink text-white" : "hover:bg-neutral-100"
          }`}
        >
          <BaseGlyph base={b} size="h-8 w-8" selected={selected === b.id} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-medium">{b.short}</span>
            <span className={`block truncate text-[12px] ${selected === b.id ? "text-white/70" : "text-neutral-500"}`}>
              {b.branch} · {b.city}
            </span>
          </span>
          <span className={`font-mono text-[11px] ${selected === b.id ? "text-brass" : "text-neutral-500"}`}>{b.homes_within}</span>
        </button>
      </li>
    ))}
  </ul>
);

/**
 * Interactive 3D installation explorer: satellite imagery, raised buildings, military land outlines,
 * cinematic fly-to + orbit, and an auto tour of every base.
 * variant: "page" (full screen under a transparent header) | "section" (embedded block)
 */
export const BaseExplorer = ({ className = "h-[720px]", variant = "section", initialBaseId = null, testId = "base-explorer" }) => {
  const navigate = useNavigate();
  const bases = useBases();
  const allParams = useMemo(() => ({}), []);
  const { data } = useListings(allParams);
  const [selected, setSelected] = useState(null);
  const [region, setRegion] = useState("Hampton Roads");
  const [orbit, setOrbit] = useState(false);
  const [focus, setFocus] = useState(null);
  const [touring, setTouring] = useState(false);
  const [radius, setRadius] = useState(10);
  const [activeKey, setActiveKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const [mapActive, setMapActive] = useState(variant !== "section");
  const orbitTimer = useRef(null);
  const base = bases.find((b) => b.id === selected);
  const all = useMemo(() => data?.listings || [], [data]);
  const nearby = useMemo(() => (base ? homesNear(base, all, radius) : []), [base, all, radius]);

  const stopMotion = () => {
    clearTimeout(orbitTimer.current);
    setOrbit(false);
    setTouring(false);
  };

  const select = useCallback((id, fromTour = false) => {
    const i = bases.findIndex((x) => x.id === id);
    if (i < 0) return;
    clearTimeout(orbitTimer.current);
    if (!fromTour) setTouring(false);
    setSelected(id);
    setActiveKey(null);
    setRegion(bases[i].region);
    setOrbit(false);
    setFocus({ ...baseCamera(bases[i], i), padding: viewPad() });
    orbitTimer.current = setTimeout(() => setOrbit(true), 4600);
  }, [bases]);

  const pick = (home) => {
    stopMotion();
    setActiveKey(home.listing_key);
    setFocus(pairCamera(base, home, pairPad()));
  };

  const changeRadius = (r) => {
    stopMotion();
    setRadius(r);
    setActiveKey(null);
    setFocus({ lng: base.lng, lat: base.lat, zoom: zoomForRadius(r) + 0.4, pitch: 50, duration: 1800, padding: viewPad(), id: `r-${base.id}-${r}-${Date.now()}` });
  };

  const overview = (r = region) => {
    clearTimeout(orbitTimer.current);
    setTouring(false);
    setSelected(null);
    setActiveKey(null);
    setOrbit(false);
    setRegion(r);
    const v = REGION_VIEWS[r];
    setFocus({ lng: v.center[0], lat: v.center[1], zoom: v.zoom, pitch: v.pitch, bearing: v.bearing, duration: 2400, id: `ov-${r}-${Date.now()}` });
  };

  useEffect(() => {
    if (initialBaseId && bases.length) select(initialBaseId);
  }, [initialBaseId, bases.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!touring || !bases.length) return undefined;
    let i = Math.max(0, bases.findIndex((b) => b.id === selected));
    if (!selected) select(bases[0].id, true);
    const t = setInterval(() => {
      i = (i + 1) % bases.length;
      select(bases[i].id, true);
    }, 10000);
    return () => clearInterval(t);
  }, [touring, bases.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(orbitTimer.current), []);

  const pinClick = (key) => {
    const home = nearby.find((h) => h.listing_key === key);
    if (home) pick(home);
    else navigate(`/listing/${key}`);
  };

  const top = variant === "page" ? "lg:top-20" : "lg:top-4";
  const regionBases = bases.filter((b) => b.region === region);

  return (
    <div className={`relative overflow-hidden bg-ink ${className}`} data-testid={testId}>
      <HomeMap
        testId={`${testId}-map`}
        listings={base ? nearby : all}
        bases={bases}
        selectedBaseId={selected}
        radius={selected ? radius : null}
        hoverKey={hoverKey}
        activeKey={activeKey}
        onBaseClick={(id) => select(id)}
        onListingClick={pinClick}
        focus={focus}
        orbit={orbit}
        onOrbitChange={(v) => { setOrbit(v); if (!v) setTouring(false); }}
        initialView={REGION_VIEWS["Hampton Roads"]}
        controlsClass={variant === "page" ? "top-[132px] lg:top-20" : "top-16 lg:top-3"}
        interactive={mapActive}
      />
      {!mapActive && <button type="button" onClick={() => setMapActive(true)} className="absolute inset-0 z-[5] grid cursor-pointer place-items-center bg-transparent" data-testid={`${testId}-activate-map`}><span className="border border-white/70 bg-ink/90 px-5 py-3 text-sm text-white shadow-lg transition-[background-color,transform] hover:scale-[1.02] hover:bg-ink">Click to explore the 3D map</span></button>}

      {/* Desktop control panel */}
      <aside className={`absolute bottom-4 left-4 z-10 hidden w-[330px] flex-col rounded-md bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.55)] lg:flex ${top}`} data-testid="base-panel">
        <div className="border-b border-neutral-200 p-5">
          <p className="eyebrow">Installation explorer</p>
          <h3 className="mt-1 font-display text-2xl text-ink">Bases & shipyards</h3>
          <div className="mt-4 grid grid-cols-2 rounded-full bg-neutral-100 p-1 text-[13px]">
            {REGIONS.map((r) => (
              <button key={r} onClick={() => overview(r)} data-testid={`region-tab-${r.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full py-1.5 transition-colors ${region === r ? "bg-white font-medium text-ink shadow-sm" : "text-neutral-600"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <p className="px-3 pb-2 pt-1 text-right font-mono text-[10px] uppercase tracking-widest text-neutral-400">Homes ≤ 10 mi</p>
          <BaseList bases={regionBases} selected={selected} onSelect={(id) => select(id)} />
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-neutral-200 p-3">
          <button onClick={() => setTouring((t) => !t)} data-testid="base-tour-toggle"
            className="flex h-10 items-center justify-center gap-2 rounded-full bg-ink text-[14px] text-white transition-colors hover:bg-neutral-800">
            {touring ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />} {touring ? "Pause tour" : "Tour all bases"}
          </button>
          <button onClick={() => overview()} data-testid="base-overview"
            className="flex h-10 items-center justify-center gap-2 rounded-full border border-neutral-300 text-[14px] text-ink hover:border-ink">
            <Globe2 className="h-4 w-4" /> Overview
          </button>
        </div>
      </aside>

      {/* Mobile chips */}
      <div className={`absolute inset-x-0 z-10 flex gap-2 overflow-x-auto px-4 no-scrollbar lg:hidden ${variant === "page" ? "top-20" : "top-4"}`}>
        <button onClick={() => setTouring((t) => !t)} data-testid="base-tour-toggle-mobile"
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] text-white shadow">
          {touring ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />} Tour
        </button>
        {bases.map((b) => (
          <button key={b.id} onClick={() => select(b.id)} data-testid={`base-chip-${b.id}`}
            className={`shrink-0 rounded-full px-4 py-2 text-[13px] shadow ${selected === b.id ? "bg-ink text-white" : "bg-white text-ink"}`}>
            {b.short}
          </button>
        ))}
      </div>

      {base && (
        <div className="pointer-events-none absolute inset-x-3 bottom-3 z-10 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[440px] lg:right-20">
          <BaseInfoCard base={base} radius={radius} onRadius={changeRadius} homes={nearby} activeKey={activeKey}
            onHover={setHoverKey} onPick={pick} onClose={() => overview(base.region)} sample={data?.sample} />
        </div>
      )}
      {!base && (
        <div className="pointer-events-none absolute bottom-6 right-6 z-10 hidden max-w-sm rounded-md bg-white/95 p-4 text-[14px] text-neutral-700 shadow-lg backdrop-blur sm:block lg:right-20" data-testid="base-explorer-hint">
          Pick an installation to fly in for a 3D look. Gold outlines mark military land; rings show 5, 10 and 15-mile distances to homes. Drag to rotate, scroll to zoom.
        </div>
      )}
    </div>
  );
};
